/**
 * Estoque.jsx — Visão consolidada do estoque atual
 * Novidades:
 *  - Checkbox por linha para seleção múltipla
 *  - Barra flutuante "Gerar etiquetas (N)" ao selecionar
 *  - Ação individual "Gerar etiqueta" no menu "..."
 *  - Modal de etiqueta: escolhe template + prévia + download/impressão
 *  - Edição do valor de venda mantida via Swal
 */

import {
  useEffect,
  useState,
  useContext,
  useCallback,
  useMemo,
  memo,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { AuthContext } from "../../context/AuthContext";
import Swal from "sweetalert2";
import api from "../../api/api";
import "./Estoque.scss";

// ─── Constantes ───────────────────────────────────────────────────────────────

const LIMITE_BAIXO = 10;
const LIMITE_ATENCAO = 20;

const STATUS_OPTIONS = [
  { value: "", label: "Todos os status" },
  { value: "normal", label: "Normal" },
  { value: "atencao", label: "Atenção" },
  { value: "baixo", label: "Baixo" },
  { value: "sem_estoque", label: "Sem estoque" },
];

const PODE_EDITAR = ["admin", "superadmin"];

// ─── Utilitários ──────────────────────────────────────────────────────────────

const formatCurrency = (v) =>
  Number(v ?? 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const formatDate = (s) => (s ? new Date(s).toLocaleDateString("pt-BR") : "-");

const formatDateTime = (s) =>
  s
    ? new Date(s).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";

const getStatus = (q) => {
  const n = Number(q);
  if (n <= 0) return "sem_estoque";
  if (n <= LIMITE_BAIXO) return "baixo";
  if (n <= LIMITE_ATENCAO) return "atencao";
  return "normal";
};

const STATUS_META = {
  normal: { label: "Normal", cls: "badge--success" },
  atencao: { label: "Atenção", cls: "badge--warning" },
  baixo: { label: "Baixo", cls: "badge--danger" },
  sem_estoque: { label: "Sem estoque", cls: "badge--muted" },
};

// ─── Sub-componentes de tabela ────────────────────────────────────────────────

const SkeletonRow = ({ cols }) => (
  <tr className="estoque-table__skeleton-row" aria-hidden="true">
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i}>
        <div className="skeleton skeleton--text" />
      </td>
    ))}
  </tr>
);

const EmptyState = ({ colSpan }) => (
  <tr>
    <td colSpan={colSpan}>
      <div className="empty-state" role="status">
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          aria-hidden="true"
        >
          <rect x="2" y="7" width="20" height="14" rx="2" />
          <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
          <line x1="12" y1="12" x2="12" y2="16" />
          <line x1="10" y1="14" x2="14" y2="14" />
        </svg>
        <p className="empty-state__text">Nenhum produto encontrado.</p>
      </div>
    </td>
  </tr>
);

const StatusBadge = memo(({ quantidade }) => {
  const s = getStatus(quantidade);
  const m = STATUS_META[s];
  return <span className={`estoque-badge ${m.cls}`}>{m.label}</span>;
});
StatusBadge.displayName = "StatusBadge";

const QtyBadge = memo(({ quantidade }) => {
  const s = getStatus(quantidade);
  const cls = {
    normal: "badge--success",
    atencao: "badge--warning",
    baixo: "badge--danger",
    sem_estoque: "badge--muted",
  }[s];
  return (
    <span className={`estoque-badge ${cls}`}>
      {Number(quantidade).toLocaleString("pt-BR")} un
    </span>
  );
});
QtyBadge.displayName = "QtyBadge";

// 2. Novo componente RowMenu — colar antes de EstoqueRow
function RowMenu({ anchorEl, onClose, children }) {
  const [pos, setPos] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!anchorEl) return;
    const r = anchorEl.getBoundingClientRect();
    const menuH = menuRef.current?.offsetHeight ?? 120;
    const spaceBelow = window.innerHeight - r.bottom;
    const openUp = spaceBelow < menuH + 8;
    setPos({
      top: openUp
        ? r.top + window.scrollY - menuH - 4
        : r.bottom + window.scrollY + 4,
      right: window.innerWidth - r.right,
    });
  }, [anchorEl]);

  if (!anchorEl) return null;
  return createPortal(
    <>
      <div
        style={{ position: "fixed", inset: 0, zIndex: 999 }}
        onClick={onClose}
      />
      <div
        ref={menuRef}
        className="row-menu"
        style={{ position: "absolute", zIndex: 1000, ...pos }}
      >
        {children}
      </div>
    </>,
    document.body,
  );
}

// ─── Linha da tabela ──────────────────────────────────────────────────────────

// ─── Linha da tabela ──────────────────────────────────────────────────────────

const EstoqueRow = memo(
  ({
    item,
    podeEditar,
    selected,
    onSelect,
    onEditarVenda,
    onGerarEtiqueta,
  }) => {
    const [menuAnchor, setMenuAnchor] = useState(null);
    const btnRef = useRef(null);
    const temValor = Boolean(item.valor_venda);

    return (
      <tr className={selected ? "row--selected" : ""}>
        {/* Checkbox */}
        <td data-label="" className="cell--check">
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onSelect(item.produto_id)}
            aria-label={`Selecionar ${item.produto_nome}`}
            className="row-checkbox"
          />
        </td>
        <td data-label="Foto" className="cell--foto">
          {item.imagem_url ? (
            <img
              src={item.imagem_url}
              alt={item.produto_nome}
              className="item-thumb"
            />
          ) : (
            <span className="item-thumb-placeholder" aria-hidden="true">
              📦
            </span>
          )}
        </td>

        <td data-label="Produto" className="cell--produto">
          {item.produto_nome}
        </td>
        <td data-label="EAN" className="cell--secondary">
          {item.ean || "-"}
        </td>
        <td data-label="Quantidade">
          <QtyBadge quantidade={item.quantidade} />
        </td>
        <td data-label="Status">
          <StatusBadge quantidade={item.quantidade} />
        </td>
        <td data-label="Valor venda" className="cell--price">
          {item.valor_venda ? (
            formatCurrency(item.valor_venda)
          ) : (
            <span className="cell--empty">-</span>
          )}
        </td>
        <td data-label="Validade" className="cell--secondary">
          {formatDate(item.data_validade)}
        </td>
        <td data-label="Atualização" className="cell--secondary">
          {formatDateTime(item.data_atualizacao)}
        </td>

        {/* Ações */}
        <td data-label="Ações" className="cell--actions">
          <button
            ref={btnRef}
            className="btn-menu btn--sm"
            onClick={() => setMenuAnchor((a) => (a ? null : btnRef.current))}
            aria-label="Mais ações"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="5" cy="12" r="2" />
              <circle cx="12" cy="12" r="2" />
              <circle cx="19" cy="12" r="2" />
            </svg>
          </button>

          <RowMenu anchorEl={menuAnchor} onClose={() => setMenuAnchor(null)}>
            {/* Editar valor de venda */}
            <button
              className="row-menu__item"
              onClick={() => {
                setMenuAnchor(null);
                onEditarVenda(item.id, item.valor_venda);
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                {temValor ? (
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z" />
                ) : (
                  <>
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </>
                )}
              </svg>
              {temValor ? "Editar valor de venda" : "Adicionar valor de venda"}
            </button>

            <div className="row-menu__divider" />

            {/* Gerar etiqueta */}
            <button
              className="row-menu__item row-menu__item--accent"
              onClick={() => {
                setMenuAnchor(null);
                onGerarEtiqueta([item.produto_id]);
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                <line x1="7" y1="7" x2="7.01" y2="7" />
              </svg>
              Gerar etiqueta
            </button>
          </RowMenu>
        </td>
      </tr>
    );
  },
);
EstoqueRow.displayName = "EstoqueRow";

// ─── Modal de Etiqueta ────────────────────────────────────────────────────────

// Substitui a regex frágil por um parser de atributos
function parseTags(xml) {
  const result = [];
  const tagRegex = /<texto([^>]*)>([\s\S]*?)<\/texto>/g;
  let match;
  while ((match = tagRegex.exec(xml)) !== null) {
    const attrs = match[1];
    const conteudo = match[2].trim();
    const get = (nome) => {
      const m = attrs.match(new RegExp(`${nome}="([^"]*)"`));
      return m ? m[1] : null;
    };
    result.push({
      x: get("x") ?? "0",
      y: get("y") ?? "0",
      tamanho: get("tamanho") ?? "3",
      negrito: get("negrito") === "true",
      texto: conteudo,
    });
  }
  return result;
}

function ModalEtiqueta({ produtoIds, clienteId, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [etiquetas, setEtiquetas] = useState([]);
  const [loadingTmpl, setLoadingTmpl] = useState(true);
  const [loadingGerar, setLoadingGerar] = useState(false);
  const [erro, setErro] = useState(null);

  // Carrega templates de etiqueta disponíveis
  useEffect(() => {
    if (!clienteId) return;
    (async () => {
      try {
        const { data } = await api.get(`/clientes/${clienteId}/templates`);
        const etiquetaTmpls = [
          ...(data.templates ?? []).filter(
            (t) => t.tipo === "etiqueta_produto" && t.ativo,
          ),
          ...(data.templates_sistema ?? []).filter(
            (t) => t.tipo === "etiqueta_produto",
          ),
        ];
        setTemplates(etiquetaTmpls);
        if (etiquetaTmpls.length > 0)
          setTemplateId(String(etiquetaTmpls[0].id));
      } catch {
        setErro("Não foi possível carregar os templates.");
      } finally {
        setLoadingTmpl(false);
      }
    })();
  }, [clienteId]);

  const handleGerar = async () => {
    setLoadingGerar(true);
    setErro(null);
    try {
      const { data } = await api.post(
        `/clientes/${clienteId}/imprimir/etiqueta`,
        {
          produto_ids: produtoIds,
          template_id: templateId ? Number(templateId) : undefined,
        },
      );
      setEtiquetas(data.etiquetas ?? []);
    } catch (e) {
      setErro(e.response?.data?.error ?? "Erro ao gerar etiquetas.");
    } finally {
      setLoadingGerar(false);
    }
  };

  // Abre janela de impressão com as etiquetas renderizadas como HTML visual
  const handleImprimir = () => {
    const parseNum = (s) => Number(String(s ?? "0").replace(/[^\d.]/g, ""));

    const etiquetasHtml = etiquetas
      .map(({ xml }) => {
        const largura = parseNum(xml.match(/largura="([^"]+)"/)?.[1] ?? "100");
        const altura = parseNum(xml.match(/altura="([^"]+)"/)?.[1] ?? "50");
        const textos = parseTags(xml);

        const spans = textos
          .map(
            (t) =>
              `<span style="position:absolute;left:${t.x}mm;top:${t.y}mm;font-size:${t.tamanho}pt;font-weight:${t.negrito ? 700 : 400};font-family:monospace;white-space:nowrap;line-height:1.2;">${t.texto}</span>`,
          )
          .join("");

        return `<div style="position:relative;width:${largura}mm;height:${altura}mm;overflow:hidden;page-break-after:always;break-after:page;box-sizing:border-box;">${spans}</div>`;
      })
      .join("");

    const primeira = etiquetas[0]?.xml ?? "";
    const parseNum0 = (s) => Number(String(s ?? "0").replace(/[^\d.]/g, ""));
    const pgW = parseNum0(primeira.match(/largura="([^"]+)"/)?.[1] ?? "100");
    const pgH = parseNum0(primeira.match(/altura="([^"]+)"/)?.[1] ?? "50");

    const win = window.open("", "_blank");
    win.document.write(`<!DOCTYPE html>
<html><head><title>Etiquetas</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  @page { size: ${pgW}mm ${pgH}mm; margin: 0; }
  @media print {
    div { page-break-after:always; break-after:page; }
    div:last-child { page-break-after:avoid; break-after:avoid; }
  }
</style>
</head><body>${etiquetasHtml}<script>window.onload=()=>{ window.print(); }<\/script></body></html>`);
    win.document.close();
  };

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-box" style={{ maxWidth: 680 }}>
        {/* Header */}
        <div className="modal-header">
          <div>
            <h2 className="modal-title">Gerar Etiquetas</h2>
            <p className="modal-subtitle">
              {produtoIds.length} produto{produtoIds.length !== 1 ? "s" : ""}{" "}
              selecionado{produtoIds.length !== 1 ? "s" : ""}
            </p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Fechar">
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {loadingTmpl ? (
            <div className="modal-loading">
              <span className="spinner" /> Carregando templates…
            </div>
          ) : (
            <>
              {/* Seleção de template */}
              <div style={{ marginBottom: 16 }}>
                <label className="field-label">Modelo de etiqueta</label>
                {templates.length === 0 ? (
                  <p style={{ fontSize: 13, color: "#EF4444", margin: 0 }}>
                    Nenhum template de etiqueta encontrado. Crie um em Templates
                    de Impressão.
                  </p>
                ) : (
                  <select
                    value={templateId}
                    onChange={(e) => {
                      setTemplateId(e.target.value);
                      setEtiquetas([]);
                    }}
                    className="field-select"
                  >
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.nome}
                        {t.is_system ? " (sistema)" : ""}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {erro && <div className="modal-erro">{erro}</div>}

              {/* Botão gerar */}
              {templates.length > 0 && etiquetas.length === 0 && (
                <button
                  className="btn-primary"
                  onClick={handleGerar}
                  disabled={loadingGerar}
                  style={{ width: "100%" }}
                >
                  {loadingGerar ? (
                    <>
                      <span className="spinner spinner--light" /> Gerando…
                    </>
                  ) : (
                    <>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        style={{ marginRight: 6 }}
                      >
                        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                        <line x1="7" y1="7" x2="7.01" y2="7" />
                      </svg>
                      Gerar etiquetas
                    </>
                  )}
                </button>
              )}

              {/* Prévia */}
              {etiquetas.length > 0 && (
                <div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 12,
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#374151",
                      }}
                    >
                      Prévia ({etiquetas.length} etiqueta
                      {etiquetas.length !== 1 ? "s" : ""})
                    </p>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button
                        className="btn-ghost btn--sm"
                        onClick={() => setEtiquetas([])}
                      >
                        Refazer
                      </button>
                      <button
                        className="btn-primary btn--sm"
                        onClick={handleImprimir}
                      >
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          style={{ marginRight: 4 }}
                        >
                          <polyline points="6 9 6 2 18 2 18 9" />
                          <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                          <rect x="6" y="14" width="12" height="8" />
                        </svg>
                        Imprimir
                      </button>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 12,
                      maxHeight: 380,
                      overflowY: "auto",
                      padding: "12px",
                      background: "#F9FAFB",
                      borderRadius: 8,
                      border: "1px solid #E5E7EB",
                    }}
                  >
                    {etiquetas.map(({ produto_id, produto_nome, xml }) => (
                      <EtiquetaPreview
                        key={produto_id}
                        xml={xml}
                        nome={produto_nome}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Prévia de uma etiqueta ───────────────────────────────────────────────────

function EtiquetaPreview({ xml, nome }) {
  // Extrai número puro removendo unidades (ex: "100mm" → 100)
  const parseNum = (s) => Number(String(s ?? "0").replace(/[^\d.]/g, ""));

  const larguraMm = parseNum(xml.match(/largura="([^"]+)"/)?.[1] ?? "100");
  const alturaMm = parseNum(xml.match(/altura="([^"]+)"/)?.[1] ?? "50");

  // Escala: quantos px por mm na prévia
  const maxW = 260;
  const escala = Math.min(maxW / larguraMm, 3); // px por mm

  const textos = parseTags(xml);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
      }}
    >
      <div
        style={{
          position: "relative",
          width: larguraMm * escala,
          height: alturaMm * escala,
          background: "#fff",
          border: "1px solid #D1D5DB",
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          flexShrink: 0,
        }}
      >
        {textos.map((t, i) => (
          <span
            key={i}
            style={{
              position: "absolute",
              left: parseNum(t.x) * escala, // mm → px
              top: parseNum(t.y) * escala, // mm → px
              fontSize: parseNum(t.tamanho) * escala * 0.35, // pt em mm → px (1pt ≈ 0.35mm)
              fontWeight: t.negrito ? 700 : 400,
              fontFamily: "monospace",
              whiteSpace: "nowrap",
              color: "#111",
              lineHeight: 1.2,
            }}
          >
            {t.texto}
          </span>
        ))}
      </div>
      <span
        style={{
          fontSize: 10,
          color: "#9CA3AF",
          maxWidth: larguraMm * escala,
          textAlign: "center",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {nome}
      </span>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

const Estoque = () => {
  const { user } = useContext(AuthContext);
  const [estoques, setEstoques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFiltro, setStatusFiltro] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [modalEtiqueta, setModalEtiqueta] = useState(null); // null | { produtoIds: [] }

  const podeEditar = PODE_EDITAR.includes(user?.nivel_acesso);
  const clienteId = JSON.parse(localStorage.getItem("usuario"))?.cliente_id;

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchEstoque = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/estoque");
      setEstoques(res.data);
    } catch {
      Swal.fire("Erro", "Não foi possível carregar o estoque.", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEstoque();
  }, [fetchEstoque]);

  // ── Editar valor de venda ──────────────────────────────────────────────────

  const handleEditarVenda = useCallback(async (id, atual) => {
    const { value: novoValor } = await Swal.fire({
      title: atual ? "Alterar valor de venda" : "Adicionar valor de venda",
      input: "number",
      inputLabel: "Valor de venda (R$):",
      inputValue: atual || "",
      inputAttributes: { min: 0, step: 0.01 },
      showCancelButton: true,
      confirmButtonText: "Salvar",
      cancelButtonText: "Cancelar",
      inputValidator: (v) => (!v ? "Informe um valor válido!" : undefined),
    });

    if (!novoValor) return;

    try {
      await api.put(`/estoque/${id}/valor-venda`, { valor_venda: novoValor });
      setEstoques((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, valor_venda: novoValor } : item,
        ),
      );
      Swal.fire({
        icon: "success",
        title: "Atualizado!",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire(
        "Erro",
        "Não foi possível atualizar o valor de venda.",
        "error",
      );
    }
  }, []);

  // ── Seleção ────────────────────────────────────────────────────────────────

  const toggleSelect = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback((ids) => {
    setSelected((prev) => {
      const allSelected = ids.every((id) => prev.has(id));
      if (allSelected) return new Set();
      return new Set(ids);
    });
  }, []);

  // ── Filtro ─────────────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    const termo = search.toLowerCase();
    return estoques.filter((item) => {
      const matchBusca =
        !termo ||
        item.produto_nome.toLowerCase().includes(termo) ||
        (item.ean && item.ean.includes(termo));
      const matchStatus =
        !statusFiltro || getStatus(item.quantidade) === statusFiltro;
      return matchBusca && matchStatus;
    });
  }, [estoques, search, statusFiltro]);

  // ── Resumo ─────────────────────────────────────────────────────────────────

  const summary = useMemo(() => {
    const totalVolumes = estoques.reduce((a, i) => a + Number(i.quantidade), 0);
    const estoqueBaixo = estoques.filter((i) =>
      ["baixo", "sem_estoque"].includes(getStatus(i.quantidade)),
    ).length;
    const valorTotal = estoques.reduce(
      (a, i) => a + Number(i.valor_venda || 0) * Number(i.quantidade),
      0,
    );
    return {
      totalProdutos: estoques.length,
      totalVolumes,
      estoqueBaixo,
      valorTotal,
    };
  }, [estoques]);

  const filteredIds = filtered.map((i) => i.produto_id); // ← era i.id
  const allSelected =
    filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));
  const someSelected = filteredIds.some((id) => selected.has(id));
  const selectedCount = filteredIds.filter((id) => selected.has(id)).length;
  const colSpan = 10; // checkbox + 7 colunas + ações

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="estoque-page">
      {/* ── Cabeçalho ─────────────────────────────────────────────────────── */}
      <div className="estoque-page__header">
        <div>
          <h1 className="estoque-page__title">Estoque atual</h1>
          <p className="estoque-page__subtitle">
            Visão consolidada por produto
          </p>
        </div>
      </div>

      {/* ── Cards de resumo ────────────────────────────────────────────────── */}
      <div className="estoque-summary">
        <div className="summary-card">
          <p className="summary-card__label">Produtos</p>
          <p className="summary-card__value">
            {summary.totalProdutos.toLocaleString("pt-BR")}
          </p>
        </div>
        <div className="summary-card">
          <p className="summary-card__label">Total em estoque</p>
          <p className="summary-card__value">
            {summary.totalVolumes.toLocaleString("pt-BR")}
          </p>
        </div>
        <div
          className={`summary-card ${summary.estoqueBaixo > 0 ? "summary-card--danger" : ""}`}
        >
          <p className="summary-card__label">Estoque baixo</p>
          <p className="summary-card__value">{summary.estoqueBaixo}</p>
        </div>
        <div className="summary-card summary-card--accent">
          <p className="summary-card__label">Valor em estoque</p>
          <p className="summary-card__value">
            {formatCurrency(summary.valorTotal)}
          </p>
        </div>
      </div>

      {/* ── Tabela ─────────────────────────────────────────────────────────── */}
      <div className="estoque-card">
        {/* Filtros */}
        <div className="estoque-filters" role="search">
          <div className="filter-search">
            <svg
              className="filter-search__icon"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="search"
              className="filter-search__input"
              placeholder="Buscar por produto ou EAN…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Buscar produto"
            />
          </div>

          <div className="filter-select-wrap">
            <select
              className="filter-select"
              value={statusFiltro}
              onChange={(e) => setStatusFiltro(e.target.value)}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {!loading && (
            <span className="filter-count">
              {filtered.length} produto{filtered.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        {/* Tabela */}
        <div
          className="estoque-table-wrapper"
          role="region"
          aria-label="Tabela de estoque"
        >
          <table className="estoque-table">
            <thead>
              <tr>
                <th scope="col" style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    className="row-checkbox"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected && !allSelected;
                    }}
                    onChange={() => toggleSelectAll(filteredIds)}
                    aria-label="Selecionar todos"
                    disabled={loading || filtered.length === 0}
                  />
                </th>
                <th scope="col">Foto</th> 
                <th scope="col">Produto</th>
                <th scope="col">EAN</th>
                <th scope="col">Quantidade</th>
                <th scope="col">Status</th>
                <th scope="col">Valor venda</th>
                <th scope="col">Validade</th>
                <th scope="col">Atualização</th>
                <th scope="col">Ações</th>
              </tr>
            </thead>
            <tbody aria-live="polite" aria-busy={loading}>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <SkeletonRow key={i} cols={colSpan} />
                ))
              ) : filtered.length === 0 ? (
                <EmptyState colSpan={colSpan} />
              ) : (
                filtered.map((item) => (
                  <EstoqueRow
                    key={item.id}
                    item={item}
                    podeEditar={podeEditar}
                    selected={selected.has(item.produto_id)}
                    onSelect={toggleSelect}
                    onEditarVenda={handleEditarVenda}
                    onGerarEtiqueta={(ids) =>
                      setModalEtiqueta({ produtoIds: ids })
                    }
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Barra flutuante de seleção ──────────────────────────────────────── */}
      {selectedCount > 0 && (
        <div className="selection-bar">
          <span className="selection-bar__count">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              style={{ marginRight: 6 }}
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            {selectedCount} produto{selectedCount !== 1 ? "s" : ""} selecionado
            {selectedCount !== 1 ? "s" : ""}
          </span>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              className="selection-bar__btn-secondary"
              onClick={() => setSelected(new Set())}
            >
              Limpar seleção
            </button>
            <button
              className="selection-bar__btn-primary"
              // Barra flutuante — linha do onClick
              onClick={() =>
                setModalEtiqueta({
                  produtoIds: filtered
                    .filter((i) => selected.has(i.produto_id)) // ← era selected.has(id)
                    .map((i) => i.produto_id),
                })
              }
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{ marginRight: 6 }}
              >
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                <line x1="7" y1="7" x2="7.01" y2="7" />
              </svg>
              Gerar etiquetas ({selectedCount})
            </button>
          </div>
        </div>
      )}

      {/* ── Modal de Etiqueta ───────────────────────────────────────────────── */}
      {modalEtiqueta && (
        <ModalEtiqueta
          produtoIds={modalEtiqueta.produtoIds}
          clienteId={clienteId}
          onClose={() => setModalEtiqueta(null)}
        />
      )}
    </div>
  );
};

export default Estoque;
